/** Who may OPEN the HR page, and who may WRITE on it: two gates, deliberately different abilities.
 *  OPEN is `hr.view` (every read the page makes answers to it; `/directory`, `/products`, `/role-history` are open to all signed-in users). WRITE stays `hr.edit`.
 *  The Salary tab, payout close-out and leave paid-off keep their own gates (`salary.view` / `salary.edit` / `leave.admin`); bank accounts and the Teams tab have theirs too -- none decided by the two constants here. */
import axios from 'axios'

import { can, type User } from './types'

/** The ability the HR nav entry (`lib/apps.ts`) and the `/hr` routes are gated on; `hrAccess.test.ts` pins the nav entry to it. */
export const HR_PAGE_ABILITY = 'hr.view'

/** The ability every HR-record write on the page needs (server: `hr.edit`). */
export const HR_WRITE_ABILITY = 'hr.edit'

export function mayOpenHr(user: User | undefined | null): boolean {
  return can(user, HR_PAGE_ABILITY)
}

/** May this viewer READ a person's bank accounts? Server: `salary.view`, which `hr.edit` and `payroll` imply. It is pay data, so `hr.view` alone is refused and the panel must not mount for it (it would only show a 403). */
export function mayReadBankAccounts(user: User | undefined | null): boolean {
  return can(user, 'salary.view')
}

/** May this viewer create or edit a personnel record? Also decides whether `/hr/new` is a route or a redirect: for a reader it would fall through to `/hr/:id` and render a blank read-only form. */
export function mayEditHr(user: User | undefined | null): boolean {
  return can(user, HR_WRITE_ABILITY)
}

/** The sentence for an HR save refused for blanking a sign-in email, or null to show the server's own; keyed on the code. */
export function emailBlankRefusal(error: unknown, t: (key: string) => string): string | null {
  const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined
  return detail?.code === 'email_needed_for_sign_in' ? t('hr.emailNeededForSignIn') : null
}
